import { computed, readonly, ref } from 'vue'
import {
  getOrganizationReadOnlyReason,
  isOrganizationReadOnlyError,
  type OrganizationReadOnlyReason,
} from '@/utils/errorHandling'

// Module-level state, like the toast queue: the condition is a property of the
// signed-in organisation rather than of a component, it is recorded from the
// axios response interceptor (which has no setup context), and the single
// ReadOnlyBanner in App.vue renders it for the whole app.
const reason = ref<OrganizationReadOnlyReason | null>(null)
const isReadOnly = ref(false)

/**
 * Records an API failure if it is the organisation write gate's refusal, and
 * ignores it otherwise.
 *
 * Called once, from the response interceptor in `ApiService`, so a refused save
 * raises the banner whatever screen it came from and whether or not that
 * screen's catch block does anything with the error.
 *
 * @returns whether the failure was the gate's refusal.
 */
export function recordOrganizationReadOnlyRefusal(err: unknown): boolean {
  if (!isOrganizationReadOnlyError(err)) return false

  isReadOnly.value = true
  // An unrecognised reason clears the previous one rather than keeping it: the
  // refusal in hand is what is true now, and naming yesterday's reason for it
  // would be worse than not naming one. See `OrganizationReadOnlyReason`.
  reason.value = getOrganizationReadOnlyReason(err) ?? null
  return true
}

/**
 * Forgets that the organisation was read-only, so the next refused write is
 * what re-establishes it.
 *
 * Called on a successful claims refresh — `refreshIdToken`, which every
 * sign-in also goes through. Nothing the frontend can read says an organisation
 * has become writable again: the state lives on `organizations` and no response
 * carries it (#4444 ships the gate with nothing that even switches it on). So
 * the honest rule is that the banner states what the backend last refused, and
 * a session that has just re-asked the backend who it is starts over — a
 * practice whose suspension was lifted stops being told otherwise without
 * having to sign out, and one whose suspension stands is told again by its next
 * write.
 */
export function clearOrganizationReadOnly(): void {
  isReadOnly.value = false
  reason.value = null
}

/**
 * Whether this organisation's writes are being refused, and why (#4445).
 *
 * ```ts
 * const { isReadOnly, reason } = useOrganizationReadOnly()
 * ```
 */
export function useOrganizationReadOnly() {
  return {
    /** True from the first refused write until the next claims refresh. */
    isReadOnly: readonly(isReadOnly),
    /** The reason the refusal carried, or `null` when it carried none we know. */
    reason: computed(() => reason.value),
  }
}
